/**
 * State remembered per history entry — the shared store behind every "back lands
 * where I left off" restore in the app.
 *
 * Two things are kept here: scroll offsets (see `scrollMemory.ts`) and map
 * cameras (see `mapMemory.ts`). They are different payloads with identical
 * navigation semantics, so they share one store rather than one each. That is not
 * merely tidier: `syncNavigation` below has to forget entries a push discarded,
 * and a second copy of the store would mean a second pruning call the router
 * could forget to make.
 *
 * vue-router cannot do any of this for us. Both its save and its restore paths
 * are hard-wired to the window (`computeScrollPosition` reads `window.scrollY`,
 * `scrollToPosition` calls `window.scrollTo`), and this app's window never
 * scrolls — `AppFrame` is `h-dvh overflow-hidden` and every screen scrolls inside
 * a descendant. So `scrollBehavior`'s `savedPosition` has always been
 * `{ top: 0 }`, and always will be.
 *
 * What we DO reuse is vue-router's notion of entry identity, because the
 * navigation semantics we want fall straight out of it with no per-screen
 * special-casing. vue-router keys its own map as `history.state.position + path`:
 *
 *   push    → `position` is incremented, so the key is new     → nothing stored → fresh
 *   back    → lands on an existing position + path             → hit            → restore
 *   replace → position is deliberately preserved, path changes → one slot per tab
 *
 * That last line is what makes `ScreenTabs` (which navigates with `replace`) give
 * every tab its own remembered state for free.
 *
 * This module is deliberately free of both DOM and router imports so the keying
 * and pruning rules can be tested as plain functions.
 */

/**
 * Capped so a long session cannot grow the map without bound. Map iteration is
 * insertion-ordered, so the first key is always the least recently written.
 *
 * Exported so tests can exercise the boundary without hardcoding the number:
 * one history entry can occupy several slots (a scroller, a map, a search field),
 * and the cap is expected to move as more kinds of state join.
 */
export const MAX_ENTRIES = 120

const entries = new Map<string, unknown>()

/** Position of the entry the previous navigation landed on; -1 before the first. */
let lastPosition = -1

/**
 * `position`, `namespace` and `id` cannot contain the separator, but `fullPath`
 * can — query values are not escaped, so `/plan?to=a|b` is a legal path. Keeping
 * `position` first and never splitting past it means the prefix stays unambiguous
 * whatever the path holds.
 *
 * `namespace` is what keeps two kinds of state from colliding on one entry: a
 * scroll container and a map on the same screen may legitimately share an `id`.
 */
export function historyKey(
  position: number,
  namespace: string,
  id: string,
  fullPath: string,
): string {
  return `${position}|${namespace}|${id}|${fullPath}`
}

/**
 * `history.state.position` for the current entry, or 0 when there is none.
 * `createMemoryHistory` populates no position at all, so every test entry shares
 * slot 0 and stays distinguished by namespace, path and id alone.
 */
export function positionOfState(state: unknown): number {
  const position = (state as { position?: unknown } | null | undefined)?.position
  return typeof position === 'number' ? position : 0
}

/**
 * The cast is unchecked by design: the payload belongs to whichever namespace
 * wrote it, and the typed wrapper modules (`scrollMemory`, `mapMemory`) are the
 * ones that pair a key with its payload shape.
 */
export function readHistoryEntry<T>(key: string): T | undefined {
  return entries.get(key) as T | undefined
}

export function writeHistoryEntry(key: string, value: unknown): void {
  // Re-inserting refreshes recency, so the screen being actively used is never
  // the one the cap evicts.
  entries.delete(key)
  entries.set(key, value)

  if (entries.size > MAX_ENTRIES) {
    const oldest = entries.keys().next()
    if (!oldest.done) entries.delete(oldest.value)
  }
}

/**
 * Tell the store where a confirmed navigation landed, so it can forget entries a
 * push has just discarded.
 *
 * `position` comes from `window.history.length`, so going back and then pushing
 * REUSES the number of the entry that was just thrown away. If that push happens
 * to target the same `fullPath`, the dead entry's state would be restored on what
 * the user experiences as a brand-new screen. vue-router has the same hole.
 *
 * `isPop` is not derivable from `position` alone, and that is the whole reason it
 * is a parameter: going forward to entry 7 and pushing a new entry 7 both report
 * position 7 after arriving. Only the caller knows whether a `popstate` fired.
 *
 *   pop (back or forward) → the forward entries are still reachable → keep
 *   replace               → position is unchanged → keep, or the sibling tab's
 *                           state under this same position would be wiped
 *   push                  → position advanced → everything at or beyond it is gone
 */
export function syncNavigation(position: number, isPop: boolean): void {
  const isPush = !isPop && position > lastPosition
  lastPosition = position
  if (!isPush) return

  // Deleting during Map iteration is well defined — the iterator skips entries
  // removed before it reaches them — so no snapshot of the keys is needed.
  for (const key of entries.keys()) {
    if (Number.parseInt(key, 10) >= position) entries.delete(key)
  }
}

/**
 * Test-only, and deliberately namespace-blind. Production code has no reason to
 * forget every remembered position, and a test that wants one namespace cleared
 * always wants the others cleared too — they are keyed by the same entry.
 */
export function resetHistoryMemory(): void {
  entries.clear()
  lastPosition = -1
}
